import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet } from '../services/apiService';
import { ENDPOINTS } from '../config/api';
import StatusPill from '../components/ui/StatusPill';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';
import UiSelect from '../components/ui/Select';
import { Plus } from 'lucide-react';

const n = v => Number(v) || 0;
const fmtMoney = (v) => { try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n(v)); } catch { return `$${n(v).toLocaleString()}`; } };
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-';
const toArr = x => Array.isArray(x) ? x : (x?.items || []);

const Stat = ({ label, value, sub, tone, first }) => (
  <div style={{ paddingRight: 26, paddingLeft: first ? 0 : 26, borderLeft: first ? 'none' : '1px solid #26262B' }}>
    <div className="field-label" style={{ marginBottom: 6 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 700, color: tone || '#FAFAFA', fontFamily: '"JetBrains Mono", monospace', lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#A1A1AA', marginTop: 5 }}>{sub}</div>}
  </div>
);

export default function AccountsPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [allOrgs, setAllOrgs] = useState([]);
  const [pickedOrgId, setPickedOrgId] = useState('');

  const openPicker = async () => {
    setPickerOpen(true);
    if (allOrgs.length === 0) {
      try { setAllOrgs(toArr(await apiGet(ENDPOINTS.ADMIN.ORGANIZATIONS, { limit: 500 }))); } catch { /* picker just shows empty */ }
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { setAccounts(toArr(await apiGet(ENDPOINTS.ADMIN.ACCOUNTS))); }
    catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter(a => [a.org_name, a.contact_email].filter(Boolean).join(' ').toLowerCase().includes(q));
  }, [accounts, search]);

  const totals = useMemo(() => ({
    balance: accounts.reduce((a, s) => a + n(s.balance), 0),
    overdue: accounts.reduce((a, s) => a + n(s.overdue_amount), 0),
    overdueCount: accounts.filter(s => n(s.overdue_amount) > 0).length,
  }), [accounts]);

  return (
    <div className="page">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20, gap: 24, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#A1A1AA', marginBottom: 4 }}>Console / <strong style={{ color: '#FAFAFA' }}>Accounts</strong></div>
          <h1 style={{ fontFamily: '"Geist","Inter",system-ui,sans-serif', fontWeight: 600, fontSize: 44, letterSpacing: '-0.02em', color: '#FAFAFA', margin: 0, lineHeight: 1.05 }}>
            <em style={{ fontStyle: 'italic', background: 'linear-gradient(135deg,#60A5FA,#3B82F6 55%,#2563EB)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Account book</em>
          </h1>
          <p style={{ color: '#A1A1AA', fontSize: 14, margin: '4px 0 0' }}>Manually-tracked balances for organizations billed outside Stripe (cash / offline payment).</p>
        </div>
        <Button variant="primary" onClick={openPicker}><Plus size={15} /> Track organization</Button>
      </div>

      <Modal open={pickerOpen} onClose={() => setPickerOpen(false)} title="Track an organization" size="sm">
        <div className="flex flex-col gap-4">
          <div>
            <div className="field-label" style={{ marginBottom: 6 }}>Organization</div>
            <UiSelect
              value={pickedOrgId}
              onChange={setPickedOrgId}
              placeholder="Search organizations…"
              searchable
              options={allOrgs.map(o => ({ value: String(o.id), label: o.name }))}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setPickerOpen(false)}>Cancel</Button>
            <Button variant="primary" disabled={!pickedOrgId} onClick={() => navigate(`/accounts/${pickedOrgId}`)}>Open ledger</Button>
          </div>
        </div>
      </Modal>

      {error && <div style={{ padding: '10px 14px', background: 'rgba(239,68,68,.13)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, color: '#EF4444', fontSize: 13, marginBottom: 14 }}>{error}</div>}

      <div className="flex flex-wrap items-start" style={{ gap: 0, rowGap: 18, paddingBottom: 20, borderBottom: '1px solid #26262B', marginBottom: 22 }}>
        <Stat first label="Total balance owed" value={fmtMoney(totals.balance)} tone={totals.balance > 0 ? '#F59E0B' : undefined} />
        <Stat label="Overdue" value={fmtMoney(totals.overdue)} tone={totals.overdue > 0 ? '#EF4444' : undefined} />
        <Stat label="Accounts overdue" value={totals.overdueCount} tone={totals.overdueCount > 0 ? '#EF4444' : undefined} />
        <Stat label="Tracked organizations" value={accounts.length} />
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <input className="field-input" placeholder="Search organization, email…" value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 1, minWidth: 220 }} />
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="tbl" style={{ width: '100%' }}>
          <thead>
            <tr><th>Organization</th><th>Contact</th><th style={{ textAlign: 'right' }}>Charged</th><th style={{ textAlign: 'right' }}>Paid</th><th style={{ textAlign: 'right' }}>Balance</th><th style={{ textAlign: 'right' }}>Overdue</th><th>Status</th></tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#A1A1AA' }}>Loading…</td></tr>}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#71717A' }}>
                No tracked accounts yet. Click "Track organization" above to add the first ledger entry for one.
              </td></tr>
            )}
            {filtered.map(a => {
              const overdue = n(a.overdue_amount) > 0;
              const paidOff = n(a.balance) <= 0;
              return (
                <tr key={a.org_id} className="cursor-pointer" onClick={() => navigate(`/accounts/${a.org_id}`)}>
                  <td><span className="tbl-id">{a.org_name}</span></td>
                  <td style={{ color: '#A1A1AA', fontSize: 12 }}>{a.contact_email || '-'}</td>
                  <td className="tbl-mono" style={{ textAlign: 'right' }}>{fmtMoney(a.total_charged)}</td>
                  <td className="tbl-mono" style={{ textAlign: 'right' }}>{fmtMoney(a.total_paid)}</td>
                  <td className="tbl-mono" style={{ textAlign: 'right', fontWeight: 600, color: paidOff ? '#22C55E' : '#FAFAFA' }}>{fmtMoney(a.balance)}</td>
                  <td className="tbl-mono" style={{ textAlign: 'right', color: overdue ? '#EF4444' : '#A1A1AA' }}>{overdue ? fmtMoney(a.overdue_amount) : '-'}</td>
                  <td>
                    {paidOff
                      ? <StatusPill status="paid" />
                      : overdue
                        ? <StatusPill status="overdue" label={`Overdue since ${fmtDate(a.oldest_overdue_due_date)}`} />
                        : <StatusPill status="unpaid" />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
